import { Link } from 'react-router-dom';
import type { RiskEntry } from '../risks/types';
import type { StudyActivity } from './types';

interface StudyActivitySheetProps {
  activities: StudyActivity[];
  risksByActivity: Record<string, RiskEntry[]>;
  canEdit: boolean;
  onAdd: (parentId: string | null) => void;
  onEdit: (activity: StudyActivity) => void;
  onDelete: (activity: StudyActivity) => void;
  onAddRisk: (activity: StudyActivity) => void;
}

function flattenActivities(nodes: StudyActivity[]): StudyActivity[] {
  const rows: StudyActivity[] = [];
  const walk = (list: StudyActivity[]) => {
    for (const node of list) {
      rows.push(node);
      if (node.children.length > 0) walk(node.children);
    }
  };
  walk(nodes);
  return rows;
}

function cellValue(value: string | null | undefined, fallback = '—'): string {
  return value?.trim() ? value : fallback;
}

export function StudyActivitySheet({
  activities,
  risksByActivity,
  canEdit,
  onAdd,
  onEdit,
  onDelete,
  onAddRisk,
}: StudyActivitySheetProps) {
  const rows = flattenActivities(activities);

  return (
    <div className='overflow-x-auto rounded border border-border'>
      <table className='min-w-full text-sm'>
        <thead className='bg-muted/50 text-foreground'>
          <tr>
            <th className='px-3 py-2 text-left'>N°</th>
            <th className='px-3 py-2 text-left'>Actividad / Tarea</th>
            <th className='px-3 py-2 text-left'>Área</th>
            <th className='px-3 py-2 text-left'>SO</th>
            <th className='px-3 py-2 text-left'>Tiempo</th>
            <th className='px-3 py-2 text-left'>Modo falla</th>
            <th className='px-3 py-2 text-left'>Efecto</th>
            <th className='px-3 py-2 text-left'>Causa</th>
            <th className='px-3 py-2 text-left'>Control</th>
            <th className='px-3 py-2 text-left'>Riesgos</th>
            {canEdit && <th className='px-3 py-2'>Acciones</th>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={canEdit ? 11 : 10} className='px-3 py-4 text-center text-muted-foreground'>
                Sin actividades. Agregue 1.0, 1.1, 1.1.3…
              </td>
            </tr>
          )}
          {rows.map((row) => {
            const risks = risksByActivity[row.id] ?? [];
            const primary = risks[0];

            return (
              <tr key={row.id} className='border-t border-border hover:bg-muted/50'>
                <td className='px-3 py-2 font-mono'>{row.outlineNo}</td>
                <td className='px-3 py-2'>{row.name}</td>
                <td className='px-3 py-2'>{cellValue(primary?.managementArea?.label)}</td>
                <td className='px-3 py-2'>
                  {primary ? `${primary.probability}×${primary.impact} (${primary.level})` : '—'}
                </td>
                <td className='px-3 py-2'>{cellValue(primary?.timeHorizon)}</td>
                <td className='px-3 py-2'>{cellValue(primary?.failureMode?.label)}</td>
                <td className='px-3 py-2'>{cellValue(primary?.effect?.label)}</td>
                <td className='px-3 py-2'>{cellValue(primary?.potentialCause)}</td>
                <td className='px-3 py-2'>{cellValue(primary?.existingControl)}</td>
                <td className='px-3 py-2'>
                  {risks.length === 0 ? (
                    <span className='text-muted-foreground'>—</span>
                  ) : (
                    <div className='space-y-1'>
                      {risks.map((risk) => (
                        <Link
                          key={risk.id}
                          to={`/riesgos/${risk.id}`}
                          className='block text-primary hover:underline text-xs'
                        >
                          {risk.code}
                        </Link>
                      ))}
                    </div>
                  )}
                </td>
                {canEdit && (
                  <td className='px-3 py-2 whitespace-nowrap'>
                    <button
                      type='button'
                      className='mr-2 text-primary'
                      onClick={() => onAddRisk(row)}
                    >
                      +riesgo
                    </button>
                    <button
                      type='button'
                      className='mr-2 text-primary'
                      onClick={() => onAdd(row.id)}
                    >
                      +hijo
                    </button>
                    <button
                      type='button'
                      className='mr-2 text-muted-foreground hover:text-foreground'
                      onClick={() => onEdit(row)}
                    >
                      Editar
                    </button>
                    <button
                      type='button'
                      className='text-red-600'
                      onClick={() => onDelete(row)}
                    >
                      ×
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {canEdit && (
        <div className='border-t border-border bg-card p-2'>
          <button
            type='button'
            className='text-sm text-primary'
            onClick={() => onAdd(null)}
          >
            + Actividad raíz (1.0, 2.0…)
          </button>
        </div>
      )}
    </div>
  );
}
